/**
 * ─────────────────────────────────────────────────────────────────
 * Perfil del usuario.
 *
 * Versión móvil:
 *  - isMobile con useMediaQuery (reactivo)
 *  - Header compacto h5 en móvil
 *  - Foto de perfil: en móvil usa @capacitor/camera (cámara o galería)
 *    en vez del campo URL manual
 *  - Layout vertical en móvil (antes grid 4+8 columnas)
 *  - Campo "Foto URL" oculto en móvil
 *  - Configuración App: layout compacto en móvil, texto no se parte
 *  - Botón eliminar cuenta: bottom sheet en móvil
 *  - Todos los botones con minHeight 44px
 * ─────────────────────────────────────────────────────────────────
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Typography, Card, CardContent, Stack,
    CircularProgress, Avatar, Divider, useTheme, useMediaQuery,
    Button, TextField, Alert, Fade, InputAdornment,
    IconButton, Paper, Switch, Select, MenuItem as MuiMenuItem,
    FormControl, Slide, Dialog,
} from "@mui/material";
import PersonIcon             from "@mui/icons-material/Person";
import EmailIcon              from "@mui/icons-material/Email";
import PhotoCameraIcon        from "@mui/icons-material/PhotoCamera";
import SaveIcon               from "@mui/icons-material/Save";
import DeleteForeverIcon      from "@mui/icons-material/DeleteForever";
import ErrorOutlineIcon       from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon             from "@mui/icons-material/Cancel";
import LinkIcon               from "@mui/icons-material/Link";
import DarkModeIcon           from "@mui/icons-material/DarkMode";
// LanguageIcon — oculto hasta completar i18n
import AutoAwesomeIcon        from "@mui/icons-material/AutoAwesome";
import { useTranslation }     from "react-i18next";
import { motion }             from "framer-motion";
import { Capacitor }          from "@capacitor/core";
import api                    from "../api/axios";
import { resolveAvatarUrl }   from "../utils/url";

const MotionCard = motion(Card);
const MotionBox  = motion(Box);
const SlideUp    = (props) => <Slide direction="up" {...props} />;

/**
 * En web usamos cadena vacía para que fetch use rutas relativas
 * (nginx hace el proxy al backend sin problemas de CORS).
 * En nativo (Capacitor) apuntamos directamente a Tailscale.
 */
const API_BASE = (() => {
    const { Capacitor: Cap } = require("@capacitor/core");
    if (Cap.isNativePlatform()) {
        return process.env.NODE_ENV === "production"
            ? "https://tahuya.taile75a0b.ts.net"
            : "http://192.168.1.133:8000";
    }
    return ""; // Web: rutas relativas → nginx proxy
})();
const GET_PROFILE_PATH    = "/perfil/";
const PUT_PROFILE_PATH    = "/perfil/";
const DELETE_PROFILE_PATH = "/perfil/";

const UPLOAD_CANDIDATES = [
    "/perfil/foto",
    "/users/me/avatar",
    "/upload/avatar",
    "/upload",
    "/files/upload",
];

/**
 * Sube una foto al backend probando varias rutas.
 * Acepta un File (web) o un base64 string (Capacitor).
 */
async function uploadPhotoAuto(fileOrBase64, token, esBase64 = false) {
    const fd = new FormData();

    if (esBase64) {
        // Convierte base64 a Blob para enviarlo como FormData
        const res  = await fetch(`data:image/jpeg;base64,${fileOrBase64}`);
        const blob = await res.blob();
        fd.append("file", blob, "foto.jpg");
    } else {
        fd.append("file", fileOrBase64);
    }

    let lastErr;
    for (const path of UPLOAD_CANDIDATES) {
        try {
            const res = await fetch(`${API_BASE}${path}`, {
                method:  "POST",
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                body:    fd,
            });
            if (!res.ok) { lastErr = new Error(`HTTP ${res.status} en ${path}`); continue; }
            const data = await res.json().catch(() => ({}));
            const url  = data.url || data.foto || data.path || data.location;
            if (!url)  { lastErr = new Error(`Respuesta sin URL en ${path}`); continue; }
            return url;
        } catch (e) { lastErr = e; }
    }
    throw lastErr || new Error("No se pudo subir la imagen");
}

export default function Profile({ user: userProp = null, onProfileUpdate = null }) {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const esNativo = Capacitor.isNativePlatform();
    const { i18n } = useTranslation();

    const [darkMode, setDarkMode] = useState(localStorage.getItem("theme") === "dark");
    const [language, setLanguage] = useState(i18n.language || "es");

    /** Alterna el modo oscuro y notifica a la app */
    const toggleDarkMode = () => {
        const newMode = !darkMode ? "dark" : "light";
        setDarkMode(!darkMode);
        localStorage.setItem("theme", newMode);
        window.dispatchEvent(new Event("theme:updated"));
    };

    /** Cambia el idioma de la app */
    const changeLanguage = (e) => {
        const lang = e.target.value;
        setLanguage(lang);
        i18n.changeLanguage(lang);
        localStorage.setItem("i18nextLng", lang);
    };

    const initialUser = useMemo(() => {
        if (userProp) return userProp;
        try {
            const raw = localStorage.getItem("user");
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    }, [userProp]);

    const [user,        setUser]        = useState(initialUser);
    const [email,       setEmail]       = useState(initialUser?.email   || "");
    const [nombre,      setNombre]      = useState(initialUser?.nombre  || initialUser?.username || "");
    const [foto,        setFoto]        = useState(initialUser?.foto    || "");
    const [loading,     setLoading]     = useState(true);
    const [saving,      setSaving]      = useState(false);
    const [error,       setError]       = useState("");
    const [ok,          setOk]          = useState("");
    const [showDelete,  setShowDelete]  = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const confirmOk = confirmText.trim().toUpperCase() === "ELIMINAR";

    /** Carga el perfil del usuario desde el backend */
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const { data: u } = await api.get(GET_PROFILE_PATH);
                if (!alive) return;
                setUser(u);
                setEmail(u.email   || "");
                setNombre(u.nombre || u.username || "");
                setFoto(u.foto     || "");
                try { localStorage.setItem("user", JSON.stringify(u)); } catch {}
            } catch {
                if (!user) setError("No se pudo cargar el perfil.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    /**
     * Abre la cámara o galería nativa (Capacitor) para seleccionar foto.
     * En web usa el input file normal.
     */
    async function handleFotoNativa() {
        try {
            const { Camera, CameraSource, CameraResultType } = await import("@capacitor/camera");

            const image = await Camera.getPhoto({
                quality:      85,
                allowEditing: false,
                resultType:   CameraResultType.Base64,
                source:       CameraSource.Prompt, // pregunta: cámara o galería
            });

            if (!image.base64String) return;

            setError(""); setOk("");
            const token    = localStorage.getItem("token") || "";
            const finalUrl = await uploadPhotoAuto(image.base64String, token, true);

            setFoto(finalUrl);
            localStorage.setItem("user_foto", finalUrl);
            try {
                const stored = localStorage.getItem("user");
                if (stored) {
                    const u = JSON.parse(stored);
                    u.foto = finalUrl;
                    localStorage.setItem("user", JSON.stringify(u));
                }
            } catch {}
            window.dispatchEvent(new CustomEvent("user:updated"));
            setOk("Foto actualizada.");
        } catch (err) {
            // Usuario canceló la selección — no es un error real
            if (!err?.message?.includes("cancel")) {
                console.error(err);
                setError("No se pudo subir la imagen.");
            }
        }
    }

    /** Maneja la subida de foto desde input file (web) */
    async function handleFileChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setError(""); setOk("");
        try {
            const token    = localStorage.getItem("token") || "";
            const finalUrl = await uploadPhotoAuto(file, token);
            setFoto(finalUrl);
            localStorage.setItem("user_foto", finalUrl);
            try {
                const stored = localStorage.getItem("user");
                if (stored) {
                    const u = JSON.parse(stored);
                    u.foto = finalUrl;
                    localStorage.setItem("user", JSON.stringify(u));
                }
            } catch {}
            window.dispatchEvent(new CustomEvent("user:updated"));
            setOk("Foto actualizada.");
        } catch (err) {
            console.error(err);
            setError("No se pudo subir la imagen.");
        } finally {
            try { e.target.value = ""; } catch {}
        }
    }

    /** Guarda los cambios del perfil en el backend */
    async function handleSave(e) {
        e?.preventDefault?.();
        setError(""); setOk(""); setSaving(true);
        try {
            const payload = { nombre, username: nombre, foto };
            const { data: updated } = await api.put(PUT_PROFILE_PATH, payload);
            const normalized = {
                ...updated,
                nombre:   updated.nombre   || updated.username || nombre,
                username: updated.username || updated.nombre   || nombre,
                foto:     updated.foto ?? foto,
                email:    updated.email ?? email,
            };
            setUser(normalized);
            setNombre(normalized.nombre || normalized.username || "");
            setFoto(normalized.foto   || "");
            setEmail(normalized.email || "");
            try {
                localStorage.setItem("user",        JSON.stringify(normalized));
                localStorage.setItem("user_nombre", normalized.nombre || "");
                localStorage.setItem("user_foto",   normalized.foto   || "");
            } catch {}
            if (typeof onProfileUpdate === "function") onProfileUpdate(normalized);
            window.dispatchEvent(new CustomEvent("user:updated"));
            setOk("Perfil actualizado correctamente.");
        } catch (err) {
            console.error(err);
            setError("No se pudo actualizar el perfil.");
        } finally {
            setSaving(false);
        }
    }

    /** Elimina la cuenta del usuario */
    async function handleDelete() {
        if (!confirmOk) return;
        setError(""); setOk("");
        try {
            await api.delete(DELETE_PROFILE_PATH);
            try { localStorage.removeItem("token"); localStorage.removeItem("user"); } catch {}
            window.location.assign("/login");
        } catch (err) {
            console.error(err);
            setError("No se pudo eliminar la cuenta.");
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 2 }}>
                <CircularProgress size={52} thickness={4} />
                <Typography variant="body1" sx={{ fontWeight: 600, opacity: 0.6 }}>Cargando tu perfil...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1000, mx: "auto", p: { xs: 1.5, md: 4 } }}>

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <MotionBox initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{ fontWeight: 900, mb: isMobile ? 2 : 4, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}
                >
                    {!isMobile && <PersonIcon sx={{ fontSize: 36 }} />}
                    Mi Perfil
                </Typography>
            </MotionBox>

            {/* ── ALERTAS ─────────────────────────────────────────────── */}
            {error && <Fade in><Alert severity="error"   icon={<ErrorOutlineIcon />}       sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert></Fade>}
            {ok    && <Fade in><Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2, borderRadius: 2 }}>{ok}</Alert></Fade>}

            {/* ── SECCIÓN FOTO + NOMBRE ────────────────────────────────── */}
            <MotionCard
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                sx={{
                    borderRadius: 3,
                    border:       "1px solid",
                    borderColor:  "divider",
                    mb:           isMobile ? 2 : 3,
                    boxShadow:    "0 2px 12px rgba(0,0,0,0.06)",
                }}
            >
                <CardContent sx={{ p: isMobile ? "16px !important" : "32px !important" }}>
                    <Stack
                        direction={isMobile ? "row" : "column"}
                        spacing={isMobile ? 2 : 0}
                        alignItems="center"
                    >
                        {/* Avatar con botón cámara */}
                        <Box sx={{ position: "relative", flexShrink: 0 }}>
                            <Avatar
                                src={resolveAvatarUrl(foto)}
                                sx={{
                                    width:     isMobile ? 80 : 140,
                                    height:    isMobile ? 80 : 140,
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                                    border:    "3px solid white",
                                }}
                            />
                            {/*
                             * En móvil nativo: botón que abre cámara/galería via Capacitor
                             * En web: input file normal
                             */}
                            {esNativo ? (
                                <IconButton
                                    onClick={handleFotoNativa}
                                    sx={{
                                        position: "absolute", bottom: 2, right: 2,
                                        bgcolor:  "primary.main", color: "white",
                                        width: 32, height: 32,
                                        "&:hover": { bgcolor: "primary.dark" },
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                    }}
                                >
                                    <PhotoCameraIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            ) : (
                                <IconButton
                                    component="label"
                                    sx={{
                                        position: "absolute", bottom: 2, right: 2,
                                        bgcolor:  "primary.main", color: "white",
                                        width: 32, height: 32,
                                        "&:hover": { bgcolor: "primary.dark" },
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                    }}
                                >
                                    <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                                    <PhotoCameraIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            )}
                        </Box>

                        {/* Nombre + email */}
                        <Box sx={{ flex: 1, textAlign: isMobile ? "left" : "center", mt: isMobile ? 0 : 2 }}>
                            <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ fontWeight: 800 }}>
                                {nombre || "Usuario"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {email}
                            </Typography>
                        </Box>

                        {/* Botón guardar — en escritorio va aquí, en móvil va abajo */}
                        {!isMobile && (
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                                sx={{ mt: 3, borderRadius: 2, minHeight: 44, fontWeight: 700, textTransform: "none" }}
                            >
                                {saving ? "Guardando..." : "Guardar Cambios"}
                            </Button>
                        )}
                    </Stack>
                </CardContent>
            </MotionCard>

            {/* ── INFORMACIÓN PERSONAL ─────────────────────────────────── */}
            <MotionCard
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", mb: isMobile ? 2 : 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
            >
                <CardContent sx={{ p: isMobile ? "16px !important" : "24px !important" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
                        Información Personal
                    </Typography>

                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label="Nombre completo"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            size={isMobile ? "small" : "medium"}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><PersonIcon color="action" fontSize="small" /></InputAdornment>,
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />

                        <TextField
                            fullWidth
                            label="Correo electrónico"
                            value={email}
                            disabled
                            size={isMobile ? "small" : "medium"}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><EmailIcon color="disabled" fontSize="small" /></InputAdornment>,
                            }}
                            helperText="El correo no se puede cambiar"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />

                        {/* Campo URL de foto — solo en escritorio, en móvil se usa la cámara */}
                        {!esNativo && (
                            <TextField
                                fullWidth
                                label="Foto de perfil (URL)"
                                value={foto}
                                onChange={(e) => setFoto(e.target.value)}
                                size={isMobile ? "small" : "medium"}
                                placeholder="/static/user_2.png o https://…"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><LinkIcon color="action" fontSize="small" /></InputAdornment>,
                                }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        )}
                    </Stack>

                    {/* Botón guardar en móvil */}
                    {isMobile && (
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            sx={{ mt: 2.5, borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
                        >
                            {saving ? "Guardando..." : "Guardar Cambios"}
                        </Button>
                    )}
                </CardContent>
            </MotionCard>

            {/* ── CONFIGURACIÓN DE LA APP ──────────────────────────────── */}
            <MotionCard
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", mb: isMobile ? 2 : 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
            >
                <CardContent sx={{ p: isMobile ? "16px !important" : "24px !important" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AutoAwesomeIcon color="primary" fontSize="small" /> Configuración de la App
                    </Typography>

                    <Stack spacing={0} divider={<Divider />}>
                        {/*
                         * Modo oscuro — solo visible en la app nativa (Capacitor).
                         * En web el tema lo gestiona el sistema/navegador y no tiene
                         * sentido exponerlo aquí, ya que la web tiene su propia versión final.
                         */}
                        {esNativo && (
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.5 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: "primary.main" + "15", color: "primary.main", width: 36, height: 36 }}>
                                        <DarkModeIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Modo Oscuro</Typography>
                                        <Typography variant="caption" color="text.secondary">Tema oscuro para descansar la vista</Typography>
                                    </Box>
                                </Stack>
                                <Switch checked={darkMode} onChange={toggleDarkMode} color="primary" size={isMobile ? "small" : "medium"} />
                            </Stack>
                        )}

                        {/* Idioma — oculto hasta que i18n esté completo en todas las páginas */}
                        {/* TODO: mostrar cuando todas las páginas tengan t() aplicado */}
                    </Stack>
                </CardContent>
            </MotionCard>

            {/* ── ZONA DE PELIGRO ──────────────────────────────────────── */}
            <Box sx={{ mt: 1 }}>
                <Button
                    fullWidth={isMobile}
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => setShowDelete(true)}
                    sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none", minHeight: 44 }}
                >
                    Eliminar mi cuenta definitivamente
                </Button>
            </Box>

            {/* ── MODAL ELIMINAR CUENTA ────────────────────────────────── */}
            {isMobile ? (
                /* Móvil: bottom sheet */
                <Dialog
                    open={showDelete}
                    onClose={() => { setShowDelete(false); setConfirmText(""); }}
                    TransitionComponent={SlideUp}
                    fullWidth
                    PaperProps={{
                        sx: {
                            position: "fixed", bottom: 0, left: 0, right: 0,
                            m: 0, width: "100%", maxWidth: "100%",
                            borderRadius: "20px 20px 0 0",
                            pb: "env(safe-area-inset-bottom)",
                        },
                    }}
                    sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
                >
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
                        <Box sx={{ width: 40, height: 4, borderRadius: 99, bgcolor: "divider" }} />
                    </Box>
                    <Box sx={{ px: 2.5, pt: 1.5, pb: 3 }}>
                        <Typography variant="h6" color="error" sx={{ fontWeight: 800, mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                            <ErrorOutlineIcon fontSize="small" /> ¿Estás segura?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Esta acción es irreversible. Todos tus datos se perderán. Escribe <strong>ELIMINAR</strong> para confirmar.
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="ELIMINAR"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                        <Stack direction="row" spacing={1.5}>
                            <Button
                                fullWidth variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={() => { setShowDelete(false); setConfirmText(""); }}
                                sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                fullWidth variant="contained" color="error"
                                disabled={!confirmOk}
                                onClick={handleDelete}
                                sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
                            >
                                Confirmar
                            </Button>
                        </Stack>
                    </Box>
                </Dialog>
            ) : (
                /* Escritorio: inline */
                <Fade in={showDelete}>
                    <Paper variant="outlined" sx={{ mt: 2, p: 3, borderColor: "error.light", bgcolor: "rgba(211,47,47,0.04)", borderRadius: 3, display: showDelete ? "block" : "none" }}>
                        <Typography variant="subtitle2" color="error" sx={{ fontWeight: 800, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                            <ErrorOutlineIcon fontSize="small" /> ¿Estás absolutamente seguro?
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                            Esta acción es irreversible. Escribe <strong>ELIMINAR</strong> para confirmar.
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                size="small" placeholder="ELIMINAR"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                sx={{ flexGrow: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                            <Button variant="contained" color="error" disabled={!confirmOk} onClick={handleDelete}
                                    sx={{ borderRadius: 2, px: 4, fontWeight: 700, textTransform: "none" }}>
                                Confirmar
                            </Button>
                            <Button variant="text" startIcon={<CancelIcon />}
                                    onClick={() => { setShowDelete(false); setConfirmText(""); }}
                                    sx={{ color: "text.secondary", fontWeight: 600, textTransform: "none" }}>
                                Cancelar
                            </Button>
                        </Stack>
                    </Paper>
                </Fade>
            )}
        </Box>
    );
}